import type { TourChapter, TourContext, TourStep } from "../types";
import { MAP_RAIL_WAIT_MS } from "../resolveTarget";

const PLAYBACK = "Playback";
const MAP = "Map";
const ANNOTATING = "Annotating";
const SAVING = "Saving and sharing";

// ---- predicates (spec §4.11). Pure; exported for the table tests. ----

const pois = (c: TourContext) => c.annotations?.pois ?? [];
const pins = (c: TourContext) => c.annotations?.pins ?? [];
const frames = (c: TourContext) => c.annotations?.frames ?? [];

export function zoomDone(ctx: TourContext): boolean {
  const badge = ctx.document.querySelector(".vmc-zoom-badge");
  return badge !== null && !(badge as HTMLElement).hidden;
}
export function poiDone(ctx: TourContext, baseline: TourContext): boolean {
  return pois(ctx).length > pois(baseline).length;
}
export function nameDone(ctx: TourContext, baseline: TourContext): boolean {
  const before = new Map(pois(baseline).map((p) => [p.id, p.name]));
  return pois(ctx).some((p) => before.has(p.id) && before.get(p.id) !== p.name);
}
export function pinDone(ctx: TourContext, baseline: TourContext): boolean {
  return pins(ctx).length > pins(baseline).length;
}
export function removeDone(ctx: TourContext, baseline: TourContext): boolean {
  return pins(ctx).length < pins(baseline).length;
}
export function freezeDone(ctx: TourContext, baseline: TourContext): boolean {
  return frames(ctx).length > frames(baseline).length;
}

// ---- steps (copy: spec §5.2, the operator's voice — verbatim) ----

const playback: TourStep[] = [
  {
    id: "orientation", target: null, section: PLAYBACK, title: "Welcome to the viewer",
    body:
      "Video on the left, map on the right, and they stay in step: the helicopter is always " +
      "where the aircraft was for the frame you're looking at.\n" +
      "It goes both ways too. Click anywhere on the red track to jump the video there, or grab " +
      "the helicopter and drag it along the track to scrub.",
  },
  {
    id: "playback", target: "viewer.controls", section: PLAYBACK, title: "Playback",
    body:
      "Space plays and pauses, or just click the picture. Click the timeline to jump, drag it " +
      "to scrub with a live preview, hover it for a thumbnail. J and L skip 15 seconds either " +
      "way.",
    keys: [
      ["Space / K", "Play or pause"],
      ["J / L", "Back or forward 15 s"],
      ["← / →", "Back or forward 5 s"],
      ["M", "Mute"],
      ["F", "Fullscreen"],
      ["Shift+. / Shift+,", "Faster or slower"],
      ["Esc", "Close menus, cancel pin mode"],
    ],
  },
  {
    id: "zoom", target: "viewer.video", kind: "do", section: PLAYBACK,
    title: "Zoom into the picture",
    body: "Point at something in the video and scroll to zoom in on it. Goes up to 8×.",
    done: zoomDone,
    success:
      "Now drag to look around, and double-click to reset. While you're zoomed in, clicking " +
      "won't pause the video, so you can line up a POI without it stopping on you.",
  },
];

const map: TourStep[] = [
  {
    id: "rail", target: "viewer.rail", waitMs: MAP_RAIL_WAIT_MS, section: MAP,
    title: "Map controls",
    body:
      "The map follows the helicopter out of the box, north up. ⌖ turns the map to its " +
      "heading instead; N is back to north up. Grab the map to take control back: it stays " +
      "put until the helicopter flies out of view, then catches up.\n" +
      "See an orange shape on the map? That's the patch of ground the camera can see right " +
      "now. The last button on this rail hides it.",
  },
  {
    id: "overlays", target: "viewer.overlays", fallback: "skip", section: MAP,
    title: "Reference overlays",
    body:
      "Your team's reference layers, like asset locations or the planned capture path. Tick " +
      "one to draw it on the map. They all start off. Got your own KMZ? Add KMZ… in here, or " +
      "just drag the file onto the page, and it shows up on every flight for your team.",
  },
  {
    id: "sets", target: "viewer.set", fallback: "skip", section: MAP, title: "Flight sets",
    body:
      "This flight is one recording of several. The grey ghost tracks are the others: click " +
      "one, or use ‹ › up here, to hop between them. When a recording ends we'll offer you " +
      "the next one.",
  },
];

const annotating: TourStep[] = [
  {
    id: "poi", target: "viewer.poi", kind: "do", section: ANNOTATING,
    title: "Mark your first point of interest",
    body:
      "Find something worth flagging, pause on it, and hit POI. It lands exactly where the " +
      "helicopter is at that moment.",
    done: poiDone,
    success: "That's saved already, and its card is open. Let's give it a proper name.",
  },
  {
    id: "name", target: "viewer.card", pick: "last", kind: "do", section: ANNOTATING,
    title: "Name it",
    body:
      "Type a name in the card and click away; it saves itself. Pick a colour if you feel " +
      "like it.",
    done: nameDone,
    success:
      "Every card has a notes thread underneath for chatting with your team about it, and " +
      "Jump to takes the video back to that moment.",
  },
  {
    id: "pin", target: "viewer.pin", kind: "do", section: ANNOTATING,
    title: "Drop a pin",
    body:
      "Pins go anywhere on the map, not just under the aircraft. Hit Pin, then click the " +
      "spot.",
    done: pinDone,
    success: "You can drag a pin somewhere new any time. Now let's tidy this one away.",
  },
  {
    id: "remove", target: "viewer.card", pick: "last", kind: "do", section: ANNOTATING,
    title: "Remove it",
    body:
      "With the pin's card open, hit Delete… and then Yes. Closed it? Click the pin, then " +
      "Edit.",
    done: removeDone,
    success:
      "Gone. Only the person who made a pin or POI can edit or delete it, but anyone can " +
      "leave a note.",
  },
  {
    id: "freeze", target: "viewer.freeze", kind: "do", section: ANNOTATING, title: "Save a frame",
    body:
      "Find a frame worth keeping and hit Freeze. You get the full-resolution JPEG with the " +
      "time and position burnt in.",
    done: freezeDone,
    success:
      "It's downloaded to your computer and it's in Saved Frames for the whole team. Zoomed " +
      "in when you hit Freeze? You get the zoomed crop.",
  },
  {
    id: "pano", target: "viewer.pano", fallback: "skip", section: ANNOTATING,
    title: "Panoramas",
    body:
      "The orange markers are 360° shots from the flight. Click one and it opens in place of " +
      "the video: drag to look around, scroll to zoom. Hit Freeze while it's open and you " +
      "keep exactly the view you're looking at.",
  },
];

const saving: TourStep[] = [
  {
    id: "saving", target: "viewer.refresh", section: SAVING, title: "Saving and teammates",
    body:
      "Everything you do saves itself the moment you do it; the pill next to Refresh shows " +
      "Saving, Saved or Pending. Lose signal and your edits queue up on this device until " +
      "you're back.\n" +
      "Refresh pulls in your teammates' edits, and the Editors line shows who's been here.",
  },
  {
    id: "scope", target: "viewer.scope", fallback: "skip", section: SAVING,
    title: "Annotation scope",
    body:
      "This flight's shared with sub-client teams, and each one keeps its own set of " +
      "annotations. Switch between the owner's set and each sub-client's here; you can edit " +
      "whichever you're looking at.",
  },
  {
    id: "export", target: "viewer.export", section: SAVING, title: "Export and offline",
    body:
      "Download KMZ gives you a Google Earth file with the track, POIs, pins, panoramas and " +
      "frames.\n" +
      "Download for offline use puts the whole flight on this device so it plays with no " +
      "signal at all. You manage those downloads in the Offline library.",
  },
  {
    id: "handoff", target: null, section: SAVING, title: "That's the viewer",
    body: "By the way, that POI and that frame are real. Keep them or bin them, they're yours.",
    handoff: "Hit 📷 Saved Frames under the video and we'll carry on there.",
  },
];

export const VIEWER_CHAPTER: TourChapter = {
  id: "viewer",
  title: "Viewer",
  steps: [...playback, ...map, ...annotating, ...saving],
};
